import { Box, Divider, Typography } from '@mui/material';
import { useSnackbar } from 'notistack';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink } from 'react-router';
import { ForbiddenError, UnauthorizedError } from 'src/api/APIClient';
import { useAPI } from 'src/api/provider';
import { Button } from 'src/lib/components/Button';
import { Link } from 'src/lib/components/Link';
import { TextField } from 'src/lib/components/fields/TextField';
import useForm from 'src/lib/components/form/useForm';
import { z } from 'zod/mini';
import Container from '../Auth/components/Container';

interface FormValues {
    username: string;
}

export const ForgotPassword = () => {
    const api = useAPI();
    const { t } = useTranslation('auth.forgotPassword');
    const [isSubmitted, setIsSubmitted] = useState(false);
    const [username, setUsername] = useState('');

    const { enqueueSnackbar } = useSnackbar();

    if (isSubmitted) {
        return (
            <Container width={360}>
                <Typography variant="h2" textAlign="center" marginBottom={1}>
                    {t('resetInstructionsLabel')}
                </Typography>
                <Typography variant="body1" textAlign="center" marginBottom={4}>
                    {t('passwordResetEmailLabel', { username })}
                </Typography>
                <Typography variant="body1" textAlign="center" marginBottom={4}>
                    {t('additionalInfo')}
                </Typography>
                <Divider sx={{ marginBottom: 4 }} />
                <Box marginBottom={2}>
                    <Button
                        kind="outlined"
                        color="primary"
                        fullWidth
                        onClick={() => setIsSubmitted(false)}
                    >
                        {t('resendInstructionsLabel')}
                    </Button>
                </Box>
                <Box>
                    <RouterLink to="/login">
                        <Button kind="filled" color="secondary" fullWidth>
                            {t('returnToLoginLabel')}
                        </Button>
                    </RouterLink>
                </Box>
            </Container>
        );
    }

    const form = useForm<FormValues>({
        validationSchema: z.object({
            username: z
                .string({
                    error: (iss) => (iss.input === undefined ? 'Required' : 'Required'),
                })
                .check(z.minLength(1, 'Required')),
        }),
        initialValues: {
            username: '',
        },
        onSubmit: async (values, { setErrors, setSubmitting }) => {
            try {
                await api.post('/client/request-password-reset/', {
                    username: values.username,
                });
                setUsername(values.username);
                setIsSubmitted(true);
            } catch (err: any) {
                if (err instanceof UnauthorizedError || err instanceof ForbiddenError) {
                    setErrors({ username: err.message });
                } else {
                    enqueueSnackbar(err.message, { variant: 'error' });
                }
            } finally {
                setSubmitting(false);
            }
        },
    });

    const errors = form.submitCount > 0 ? form.errors : {};

    return (
        <Container width={360}>
            <Typography variant="h2" align="center" marginBottom={1}>
                {t('forgotPasswordTitle')}
            </Typography>
            <Typography variant="body1" align="center" marginBottom={4}>
                {t('forgotPasswordInfo')}
            </Typography>
            <form onSubmit={form.handleSubmit}>
                <Box marginBottom={4}>
                    <TextField
                        label={t('usernameLabel')}
                        name="username"
                        value={form.values.username}
                        onChange={form.handleChange}
                        error={errors.username}
                        autoFocus
                    />
                </Box>
                <Box marginBottom={2}>
                    <Button
                        type="submit"
                        kind="filled"
                        color="primary"
                        fullWidth
                        disabled={form.isSubmitting}
                    >
                        {t('sendInstructionsLabel')}
                    </Button>
                </Box>
                <Box marginBottom={2}>
                    <RouterLink to="/login">
                        <Button kind="filled" color="secondary" fullWidth>
                            {t('returnToLoginLabel')}
                        </Button>
                    </RouterLink>
                </Box>
                <Typography variant="body3" textAlign="center">
                    <Link to="/sso">{t('loginWithSSO')}</Link>
                </Typography>
            </form>
        </Container>
    );
};
